/**
 * REST API Routes only module
 *
 * The sole purpose of this file is to make sure we have all the routes
 * grouped together as classes in one file.
 *
 * Any back-end API changes that are to be made should be done here.
 *
 * Every API method does two jobs:
 * 1. Calls the API
 * 2. Validates the response using Zod & returns the validated response body
 *
 * See [Query.md](../../../ai-docs/architecture/frontend/query.md) for more information on guidelines here
 * */
import { getDeviceTimeZone } from '@electron/shared/device-timezone';
import { apiClient, HttpError } from '@littlebird/auth-client';
import {
  createProviderToolsApi,
  type ProviderToolsRequest,
} from '@littlebird/integrations';
import { createMCPClient, type OAuthReturnTo } from '@littlebird/mcp';
import {
  extractBackendErrorCode,
  FileLinkMetaSchema,
  type TranscriptRequestFailure,
} from '@littlebird/sdk';
import { type EmailDraft, EmailDraftSchema } from '@littlebird/ui-utils';
import { captureException } from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import {
  ChatCostSummarySchema,
  ChatDebugMessagesResponseSchema,
  DebugPayloadSkeletonsResponseSchema,
  DebugFullPayloadSchema,
  TraceApiResponseSchema,
  type ChatCostSummary,
  type ChatTurnTrace,
  type DebugPayloadSkeletonsResponse,
  type DebugFullPayload,
  type TraceSpan,
} from '@/features/chat-debug/chat-debug.types';
import { electronApis } from '@/features/electron-api';
import {
  type ConnectedAccount,
  ConnectedAccountSchema,
  type ConnectionCardInfo,
  ConnectionCardInfoSchema,
  ConnectIntegrationResponseSchema,
  type DisconnectIntegrationResponse,
  DisconnectIntegrationResponseSchema,
  type IntegrationInfo,
  IntegrationInfoSchema,
  type IntegrationSuggestion,
  IntegrationSuggestionSchema,
  type OnDeviceMcpCatalogEntry,
  OnDeviceMcpCatalogEntrySchema,
  type ToggleIntegrationRequest,
} from '@/features/integrations/integrations.types';
import {
  McpCallDetailSchema,
  McpCallListResponseSchema,
  McpUsageSummarySchema,
  type McpCallDetail,
  type McpCallListResponse,
  type McpUsageSummary,
} from '@/features/mcp-info/mcp-info.types';
import type { RecordingSession } from '@/features/meetings/meetings.recording-session';
import {
  type CalendarEvent,
  type CalendarEventFilterSettings,
  type Meeting,
  type MeetingId,
  MeetingSchema,
  CalendarEventsResponseSchema,
  buildCalendarEventFilterQueryParams,
  MeetingLanguageOptionSchema,
  type MeetingLanguageOption,
  type SharedMeetingSummary,
  SharedMeetingSummarySchema,
  UpdateMeetingLanguagePreferenceResponseSchema,
  type UpdateMeetingLanguagePreferenceResponse,
  type MeetingShareDraftResponse,
  MeetingShareDraftResponseSchema,
} from '@/features/meetings/meetings.types';
import {
  type EmailChangeOTPResponse,
  EmailChangeOTPResponseSchema,
  type SyncUserResponse,
  SyncUserResponseSchema,
  type ConfirmMicrosoftEmailResponse,
  ConfirmMicrosoftEmailResponseSchema,
  type VerifyEmailChangeResponse,
  VerifyEmailChangeResponseSchema,
} from '@/features/user/user.types';
import { inElectron } from '@/utils/capabilities';

import {
  type FeedbackResponse,
  FeedbackResponseSchema,
  type FeedbackType,
} from './features/feedback/feedback.types';
import {
  type WrappedResponse,
  WrappedResponseSchema,
} from './features/growth/growth.types';
import {
  type ApplyReferralCodeRequest,
  type ApplyReferralCodeResponse,
  type InviteRequest,
  type InviteResponse,
  InviteResponseSchema,
  ApplyReferralCodeResponseSchema,
  type ReferralCodeResponse,
  ReferralCodeResponseSchema,
  type ReferralStatsResponse,
  ReferralStatsResponseSchema,
} from './features/referral/referral.types';
import {
  type ApplyRetentionOfferResponse,
  ApplyRetentionOfferResponseSchema,
  type InvoiceListResponse,
  InvoiceListResponseSchema,
  type CancellationContextResponse,
  CancellationContextResponseSchema,
  type CreateCheckoutSessionRequest,
  type CreateCheckoutSessionResponse,
  type ApplyPromoCodeResponse,
  ApplyPromoCodeResponseSchema,
  CreateCheckoutSessionResponseSchema,
  type CreatePortalSessionRequest,
  type CreatePortalSessionResponse,
  CreatePortalSessionResponseSchema,
  type AutoRefillSettings,
  AutoRefillSettingsSchema,
  type CreditBalanceResponse,
  CreditBalanceResponseSchema,
  type CreditPacksResponse,
  CreditPacksResponseSchema,
  type PricingPlansResponse,
  PricingPlansResponseSchema,
  type PurchaseCreditPackRequest,
  type PurchaseCreditPackResponse,
  PurchaseCreditPackResponseSchema,
  type UpdateAutoRefillRequest,
  type SubscriptionStatusResponse,
  SubscriptionStatusResponseSchema,
  type UpgradeToPowerRequest,
  type UpgradeToPowerResponse,
  UpgradeToPowerResponseSchema,
  type UpgradeToProRequest,
  type UpgradeToProResponse,
  UpgradeToProResponseSchema,
} from './features/subscription/subscription.types';
import {
  type GraphData,
  type WorldModelDoc,
  type WorldModelDocListItem,
  WorldModelDocListItemSchema,
  WorldModelDocSchema,
} from './features/world-model/world-model.types';
import { type Chat, ChatSchema } from './query/chat';

const SignBaaResponseSchema = z.object({
  has_signed_baa: z.boolean(),
  baa_signed_name: z.string().nullable(),
  baa_signed_at: z.string().nullable(),
  already_signed: z.boolean(),
});

export type SignBaaResponse = z.infer<typeof SignBaaResponseSchema>;

const MarkJournalViewedResponseSchema = z.object({
  detail: z.string(),
  uuid: z.string(),
});

const MarkAllJournalViewedResponseSchema = z.object({
  detail: z.string(),
  count: z.number(),
});

export type MarkJournalViewedResponse = z.infer<
  typeof MarkJournalViewedResponseSchema
>;

export type MarkAllJournalViewedResponse = z.infer<
  typeof MarkAllJournalViewedResponseSchema
>;

/** Backend HTTP failure in the package-neutral shape the SDK's transcript
 * post-processing classifies on: HTTP status plus the backend's structured
 * `error_code` (FastAPI returns it as `detail.error_code`). */
class TranscriptRequestError extends Error implements TranscriptRequestFailure {
  readonly status: number;
  readonly errorCode?: string;

  constructor(cause: HttpError) {
    super(cause.message, { cause });
    this.name = 'TranscriptRequestError';
    this.status = cause.status;
    this.errorCode = extractBackendErrorCode(cause.body);
  }
}

/**
 * The Meeting Notes API class.
 * @deprecated Meeting data is now managed by the sync engine (MeetingStore).
 * Use `getStores().meetings` methods instead for CRUD operations.
 * This class is kept for backward compatibility during migration.
 */
class MeetingNotesAPI {
  /** Get all the meetings as an array */
  async getAll(): Promise<Meeting[]> {
    const r = await apiClient.get('/meetings');
    return z.array(MeetingSchema).parseAsync(r);
  }
  /** Get all the active meetings */
  async getAllActive(): Promise<Meeting[]> {
    const r = await apiClient.get('/meetings/active');
    return z.array(MeetingSchema).parseAsync(r);
  }
  /** Get a single Meeting by ID */
  async getById(id: MeetingId): Promise<Meeting> {
    const r = await apiClient.get(`/meetings/${id}`);
    return MeetingSchema.parse(r);
  }

  /** Permanently deletes meeting notes */
  delete(id: MeetingId): Promise<void> {
    return apiClient.delete(`/meetings/${id}`);
  }

  /** Uploads an audio chunk for meeting transcription */
  async uploadAudio(
    id: MeetingId,
    chunkNumber: number,
    data: FormData
  ): Promise<Meeting> {
    const r = await apiClient.postFormData(
      `/meetings/${id}/chunks/${chunkNumber}`,
      data
    );
    return MeetingSchema.parseAsync(r);
  }

  async importGranolaCsv(file: File): Promise<void> {
    const fileId = uuidv4();
    const contentType = 'text/csv';

    const upload = await apiClient.post<{ url: string }>('/get_upload_url', {
      file_metadata: {
        id: fileId,
        filename: file.name,
        content_type: contentType,
        size: file.size,
        entity: 'granola_import',
      },
    });

    const putResult = await fetch(upload.url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!putResult.ok) {
      throw new Error(
        `Granola CSV upload failed with status ${putResult.status} ${putResult.statusText}`
      );
    }

    await apiClient.post(`/finalize_upload/${fileId}`);
    await apiClient.post(`/meetings/granola_import/${fileId}`);
  }

  /** Create a Gmail draft of the meeting summary addressed to the other attendees */
  async createShareDraft(
    id: MeetingId,
    accountLabel: string
  ): Promise<MeetingShareDraftResponse> {
    const r = await apiClient.post(
      `/meetings/${id}/share-draft`,
      { account_label: accountLabel },
      // A 5xx retry re-runs the LLM rewrite (slow) and can mint a duplicate
      // draft when the first attempt succeeded upstream; the dialog has its
      // own Retry button.
      { retryOptions: { retries: 0 } }
    );
    return MeetingShareDraftResponseSchema.parseAsync(r);
  }

  async fetchRecordingRules() {
    // Bounded so a slow backend never holds up starting a recording
    const policy = await apiClient.get('/meetings/recording-policy', {
      signal: AbortSignal.timeout(2000),
    });
    return z
      .object({
        rules: z.array(
          z.object({
            kind: z.string(),
            value: z.string(),
          })
        ),
      })
      .parse(policy).rules;
  }

  async fetchTranscriptDeletionEnabled() {
    const response = await apiClient.get('/meetings/transcript-deletion');
    return z.object({ enabled: z.boolean() }).parse(response).enabled;
  }

  /** Fetch available meeting languages */
  async fetchMeetingLanguages(): Promise<MeetingLanguageOption[]> {
    const languagesDataList = await apiClient.get('/meetings/languages');

    return z.array(MeetingLanguageOptionSchema).parseAsync(languagesDataList);
  }

  /** Update user's meeting language preference */
  async updateMeetingLanguagePreference(
    language: string
  ): Promise<UpdateMeetingLanguagePreferenceResponse> {
    const updateResponse = await apiClient.post(
      '/meetings/language_preference',
      {
        language,
      }
    );

    return UpdateMeetingLanguagePreferenceResponseSchema.parseAsync(
      updateResponse
    );
  }

  async getSharedSummary(
    id: MeetingId,
    publicToken?: string,
    useAuth = false
  ): Promise<SharedMeetingSummary> {
    const r = await apiClient.get(`/meetings/${id}/shared_summary`, {
      params: publicToken ? { public_token: publicToken } : undefined,
      skipAuth: !useAuth,
    });
    return SharedMeetingSummarySchema.parseAsync(r);
  }

  /** Patch transcript delta to the meeting (appends at offset) */
  async patchTranscript(
    session: RecordingSession,
    startOffset: number,
    delta: string,
    speaker: string,
    signal?: AbortSignal
  ): Promise<{ current_length: number; current_speaker: string | null }> {
    const r = await apiClient.patch(
      `/meetings/${session.meetingId}/transcript`,
      {
        start_offset: startOffset,
        delta,
        speaker,
        device_id: session.deviceId,
        recording_session_id: session.sessionId,
      },
      {
        signal,
        retryOptions: {
          retries: 5,
          retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 30000),
          retryOn: (attempt, error, response) => {
            if (attempt >= 5) return false;
            if (error) {
              if (error instanceof DOMException && error.name === 'AbortError')
                return false;
              return true;
            }
            if (response && response.status >= 500) return true;
            return false;
          },
        },
      }
    );
    return z
      .object({
        current_length: z.number(),
        current_speaker: z.string().nullable(),
      })
      .parseAsync(r);
  }

  /** Ingest any trailing transcript remnant for the meeting */
  async ingestRemainingTranscript(id: MeetingId): Promise<void> {
    await apiClient.post(`/meetings/${id}/ingest_remaining_transcript`);
  }

  async resumeRecording(
    session: RecordingSession,
    takeoverOtherDevice?: boolean
  ): Promise<{
    transcript: string;
    currentOffset: number;
    transcriptRevision: string | null;
  }> {
    const schema = z.object({
      session_id: z.string().nullable(),
      device_id: z.string().nullable(),
      transcript_revision: z.string().nullable(),
      personified_transcript: z.string(),
      current_length: z.number(),
    });
    const path = `/meetings/${session.meetingId}/recording-session`;
    const observed = schema.parse(await apiClient.get(path));
    const claimed = schema.parse(
      await apiClient.post(path, {
        device_id: session.deviceId,
        session_id: session.sessionId,
        expected_session_id: observed.session_id,
        expected_device_id: observed.device_id,
        takeover_other_device: takeoverOtherDevice ?? false,
      })
    );
    return {
      transcript: claimed.personified_transcript,
      currentOffset: claimed.current_length,
      transcriptRevision: claimed.transcript_revision,
    };
  }

  async renewRecording(session: RecordingSession): Promise<void> {
    await apiClient.post(
      `/meetings/${session.meetingId}/recording-session/renew`,
      {
        device_id: session.deviceId,
        session_id: session.sessionId,
      },
      { signal: AbortSignal.timeout(10_000), retryOptions: { retries: 0 } }
    );
  }

  async endRecording(session: RecordingSession): Promise<void> {
    await apiClient.post(
      `/meetings/${session.meetingId}/recording-session/release`,
      {
        device_id: session.deviceId,
        session_id: session.sessionId,
      }
    );
  }

  async beginTranscriptProcessing(
    id: MeetingId,
    processingToken: string,
    expectedLength: number,
    session?: RecordingSession
  ): Promise<void> {
    await apiClient.post(`/meetings/${id}/transcript/processing/start`, {
      device_id: session?.deviceId ?? (await electronApis.system.getDeviceId()),
      processing_token: processingToken,
      expected_length: expectedLength,
      recording_session_id: session?.sessionId,
    });
  }

  async finishTranscriptProcessing(
    id: MeetingId,
    processingToken: string
  ): Promise<void> {
    await apiClient.post(`/meetings/${id}/transcript/processing/finish`, {
      processing_token: processingToken,
    });
  }

  /** Atomically replace `[startOffset, end)` of the transcript with the
   * post-processed segments. Offsets are code points, passed through untouched.
   * HTTP failures rethrow as TranscriptRequestError so the SDK can classify
   * discard-vs-retry on the status plus the backend's structured error_code. */
  async replaceTranscriptSuffix(
    id: MeetingId,
    startOffset: number,
    expectedLength: number,
    segments: Array<{ speaker: string; text: string }>,
    deviceId: string,
    processingToken?: string
  ): Promise<{ current_length: number; current_speaker: string | null }> {
    let r: unknown;
    try {
      r = await apiClient.post(`/meetings/${id}/transcript/replace_suffix`, {
        start_offset: startOffset,
        expected_length: expectedLength,
        segments,
        device_id: deviceId,
        processing_token: processingToken,
      });
    } catch (error) {
      if (error instanceof HttpError) {
        throw new TranscriptRequestError(error);
      }
      throw error;
    }
    return z
      .object({
        current_length: z.number(),
        current_speaker: z.string().nullable(),
      })
      .parseAsync(r);
  }

  /** Merge multiple meetings into one */
  async mergeMeetings(
    meetingIds: MeetingId[]
  ): Promise<{ meeting: Meeting; deleted_meeting_ids: string[] }> {
    const r = await apiClient.post('/meetings/merge', {
      meeting_ids: meetingIds,
    });
    return z
      .object({
        meeting: MeetingSchema,
        deleted_meeting_ids: z.array(z.string()),
      })
      .parseAsync(r);
  }
}

class InviteAPI {
  async sendInvites(request: InviteRequest): Promise<InviteResponse> {
    const r = await apiClient.post('/invite/send', request);
    return InviteResponseSchema.parseAsync(r);
  }
}

/** The Referral API class for referral code operations */
class ReferralAPI {
  /** Get the current user's referral code */
  async getCode(): Promise<ReferralCodeResponse> {
    const r = await apiClient.get('/referral/code');
    return ReferralCodeResponseSchema.parseAsync(r);
  }

  /** Get detailed referral statistics including referred users */
  async getStats(): Promise<ReferralStatsResponse> {
    const r = await apiClient.get('/referral/stats');
    return ReferralStatsResponseSchema.parseAsync(r);
  }

  /** Apply a referral code to the current user */
  async apply(
    request: ApplyReferralCodeRequest
  ): Promise<ApplyReferralCodeResponse> {
    const r = await apiClient.post('/referral/apply', request);
    return ApplyReferralCodeResponseSchema.parseAsync(r);
  }
}

/** The Feedback API class for back-end feedback APIs */
class FeedbackAPI {
  /** Submit user feedback */
  async submitFeedback(
    type: FeedbackType,
    feedback: string
  ): Promise<FeedbackResponse> {
    const r = await apiClient.post('feedback', {
      feedback_type: type,
      text: feedback,
    });

    return FeedbackResponseSchema.parseAsync(r);
  }

  /** Submit a data export request */
  async submitDataExportRequest(reason: string, timeFrame: string) {
    const r = await apiClient.post('data_export_request', {
      reason,
      time_frame: timeFrame,
    });
    return z.object({ status: z.string(), message: z.string() }).parseAsync(r);
  }
}

/** The User API class for back-end user APIs */
class UserAPI {
  /** Initiate email change process - sends OTP to new email */
  async initiateEmailChange(newEmail: string): Promise<EmailChangeOTPResponse> {
    const r = await apiClient.post('/change-email/initiate', {
      new_email: newEmail,
    });
    return EmailChangeOTPResponseSchema.parseAsync(r);
  }

  /** Verify OTP and complete email change */
  async verifyEmailChange(
    otp: string,
    newEmail: string
  ): Promise<VerifyEmailChangeResponse> {
    const r = await apiClient.post('/change-email/verify', {
      otp,
      new_email: newEmail,
    });
    return VerifyEmailChangeResponseSchema.parseAsync(r);
  }

  /** Resend OTP for email change */
  async resendEmailChangeOTP(
    newEmail: string
  ): Promise<EmailChangeOTPResponse> {
    const r = await apiClient.post('/change-email/resend-otp', {
      new_email: newEmail,
    });
    return EmailChangeOTPResponseSchema.parseAsync(r);
  }

  /** Set custom transcription vocabulary for meeting notes */
  async setCustomTranscriptionVocabulary(vocabulary: string[]): Promise<void> {
    await apiClient.patch('/custom_transcription_vocabulary', {
      custom_transcription_vocabulary: vocabulary,
    });
  }

  /**
   * Sync user data on login
   * Note: Uses custom auth header since token isn't in localStorage yet
   */
  async syncUser(accessToken: string): Promise<SyncUserResponse> {
    const [deviceId, platform, deviceSlug] = await Promise.all([
      electronApis.system.getDeviceId(),
      electronApis.system.getPlatform(),
      electronApis.system.getDeviceSlug(),
    ]);

    const response = await apiClient.post(
      '/sync/user',
      { device_id: deviceId, platform, device_slug: deviceSlug },
      {
        skipAuth: true,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return SyncUserResponseSchema.parseAsync(response);
  }

  /**
   * Confirm a paused Microsoft sign-in's email. The Auth0 Action links the accounts when it resumes.
   * Uses the access token from the email-code sign-in on the /verify-email page
   */
  async confirmMicrosoftEmail(
    accessToken: string,
    sessionToken: string,
    state: string
  ): Promise<ConfirmMicrosoftEmailResponse> {
    const response = await apiClient.post(
      '/account-linking/confirm-email',
      { session_token: sessionToken, state },
      {
        skipAuth: true,
        headers: { Authorization: `Bearer ${accessToken}` },
        // The page shows its own error, so skip the global error toast
        emitHttpErrorEvent: false,
      }
    );
    return ConfirmMicrosoftEmailResponseSchema.parseAsync(response);
  }

  /** Delete current user account */
  async deleteUser(): Promise<void> {
    await apiClient.post('/delete/user');
  }

  /** Self-service in-app HIPAA BAA signature */
  async signBaa(legalName: string): Promise<SignBaaResponse> {
    const r = await apiClient.post('/sign_baa', { legal_name: legalName });
    return SignBaaResponseSchema.parseAsync(r);
  }

  /** Logout user from backend */
  async logout(): Promise<void> {
    await apiClient.post('/logout');
  }
}

/** The Todo API class for back-end todo APIs */
class TodoAPI {
  async create(title: string): Promise<void> {
    const response = await apiClient.post('/action-cards/todos', { title });
    z.object({ id: z.string().uuid() }).parse(response);
  }
}

const CardWatcherRoutinesSchema = z.object({
  // The oldest watcher routine; null while the user has none
  routine_id: z.number().nullable(),
  // Every proactive agent's watcher routine. Missing from a backend older than watchers per agent
  routines: z
    .array(z.object({ routine_id: z.number(), agent: z.string() }))
    .optional(),
});

/** A proactive agent's card watcher routine */
export type CardWatcherRoutine = { routineId: number; agent: string | null };

/** Internal-only: the signed-in user's card watcher routines, one per proactive agent */
class CardWatcherAPI {
  async routines(): Promise<CardWatcherRoutine[]> {
    const r = await apiClient.get('/card-watchers/routine');
    const parsed = await CardWatcherRoutinesSchema.parseAsync(r);
    if (parsed.routines) {
      return parsed.routines.map(({ routine_id, agent }) => ({
        routineId: routine_id,
        agent,
      }));
    }
    return parsed.routine_id === null
      ? []
      : [{ routineId: parsed.routine_id, agent: null }];
  }
}

/** The Subscription API class for back-end subscription APIs */
class SubscriptionAPI {
  async getStatus(): Promise<SubscriptionStatusResponse> {
    const r = await apiClient.get('/payment/subscription-status');
    return SubscriptionStatusResponseSchema.parseAsync(r);
  }

  async getPricingPlans(): Promise<PricingPlansResponse> {
    const r = await apiClient.get('/payment/pricing-plans');
    return PricingPlansResponseSchema.parseAsync(r);
  }

  async createCheckoutSession(
    request: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse> {
    const r = await apiClient.post('/payment/create-checkout-session', request);
    return CreateCheckoutSessionResponseSchema.parseAsync(r);
  }

  async cancelSubscription(): Promise<void> {
    await apiClient.post('/payment/cancel-subscription');
  }

  async resumeSubscription(): Promise<void> {
    await apiClient.post('/payment/resume-subscription');
  }

  async createPortalSession(
    request: CreatePortalSessionRequest
  ): Promise<CreatePortalSessionResponse> {
    const r = await apiClient.post('/payment/create-portal-session', request);
    return CreatePortalSessionResponseSchema.parseAsync(r);
  }

  async getCancellationContext(): Promise<CancellationContextResponse> {
    const r = await apiClient.get('/payment/cancellation-context');
    return CancellationContextResponseSchema.parseAsync(r);
  }

  async applyRetentionOffer(): Promise<ApplyRetentionOfferResponse> {
    const r = await apiClient.post('/payment/apply-retention-offer');
    return ApplyRetentionOfferResponseSchema.parseAsync(r);
  }

  async getInvoices(): Promise<InvoiceListResponse> {
    const r = await apiClient.get('/payment/invoices');
    return InvoiceListResponseSchema.parseAsync(r);
  }

  async getCreditPacks(): Promise<CreditPacksResponse> {
    const r = await apiClient.get('/payment/credit-packs');
    return CreditPacksResponseSchema.parseAsync(r);
  }

  async getCreditBalance(): Promise<CreditBalanceResponse> {
    const r = await apiClient.get('/payment/credit-balance');
    return CreditBalanceResponseSchema.parseAsync(r);
  }

  async purchaseCreditPack(
    request: PurchaseCreditPackRequest
  ): Promise<PurchaseCreditPackResponse> {
    const r = await apiClient.post('/payment/purchase-credit-pack', request);
    return PurchaseCreditPackResponseSchema.parseAsync(r);
  }

  async purchaseTeamCreditPack(
    teamId: string,
    request: PurchaseCreditPackRequest
  ): Promise<PurchaseCreditPackResponse> {
    const r = await apiClient.post(`/team/${teamId}/wallet/purchase`, request);
    return PurchaseCreditPackResponseSchema.parseAsync(r);
  }

  async getAutoRefill(): Promise<AutoRefillSettings> {
    const r = await apiClient.get('/payment/auto-refill');
    return AutoRefillSettingsSchema.parseAsync(r);
  }

  async updateAutoRefill(
    request: UpdateAutoRefillRequest
  ): Promise<AutoRefillSettings> {
    const r = await apiClient.put('/payment/auto-refill', request);
    return AutoRefillSettingsSchema.parseAsync(r);
  }

  async applyPromoCode(code: string): Promise<ApplyPromoCodeResponse> {
    const r = await apiClient.post('/payment/apply-promo-code', { code });
    return ApplyPromoCodeResponseSchema.parseAsync(r);
  }

  async upgradeToPro(
    request: UpgradeToProRequest
  ): Promise<UpgradeToProResponse> {
    const r = await apiClient.post('/payment/upgrade-to-pro', request);
    return UpgradeToProResponseSchema.parseAsync(r);
  }

  async upgradeToPower(
    request: UpgradeToPowerRequest
  ): Promise<UpgradeToPowerResponse> {
    const r = await apiClient.post('/payment/upgrade-to-power', request);
    return UpgradeToPowerResponseSchema.parseAsync(r);
  }

  async submitStudentDiscountRequest(formData: FormData) {
    const r = await apiClient.postFormData(
      '/payment/student-discount-request',
      formData
    );
    return z
      .object({
        status: z.string(),
        message: z.string(),
        expires_at: z.string(),
      })
      .parseAsync(r);
  }
}

/** The Journal API class for back-end journal APIs */
class JournalAPI {
  /** Mark a journal as viewed by the user */
  async markViewed(uuid: string): Promise<MarkJournalViewedResponse> {
    const r = await apiClient.post(
      '/journal/mark_viewed',
      {},
      { params: { uuid } }
    );
    return MarkJournalViewedResponseSchema.parseAsync(r);
  }

  async markAllViewed(): Promise<MarkAllJournalViewedResponse> {
    const response = await apiClient.post('/journal/mark_all_viewed');
    return MarkAllJournalViewedResponseSchema.parseAsync(response);
  }
}

class WorldModelDocAPI {
  async getAll(): Promise<WorldModelDocListItem[]> {
    const r = await apiClient.get('/world_model/user-docs');
    return z.array(WorldModelDocListItemSchema).parseAsync(r);
  }

  async getById(id: string): Promise<WorldModelDoc> {
    const r = await apiClient.get(`/world_model/user-docs/${id}`);
    return WorldModelDocSchema.parseAsync(r);
  }

  async getGraphData(): Promise<GraphData> {
    const r = await apiClient.get('/world_model/graph-data');
    return r as GraphData;
  }
}

const InitialSuggestionsStatusResponseSchema = z.object({
  status: z.enum(['none', 'running', 'done']),
});
type InitialSuggestionsStatus = z.infer<
  typeof InitialSuggestionsStatusResponseSchema
>['status'];

/** Some backend responses depend on platform and version; the shared API client sends only the version. */
const platformHeaders = () => ({
  'x-platform': inElectron() ? 'desktop' : 'web',
});

/** The Integrations API class for managing connected integrations */
class IntegrationsAPI {
  /** Get available integrations for the user */
  async getAvailableIntegrations(): Promise<IntegrationInfo[]> {
    const r = await apiClient.get(
      '/integrations/available?include_on_device=true',
      { headers: platformHeaders() }
    );
    return z.array(IntegrationInfoSchema).parseAsync(r);
  }

  async resolveConnectionCard(flowRef: string): Promise<ConnectionCardInfo> {
    const r = await apiClient.get('/integrations/connection-card', {
      params: { flow_ref: flowRef },
    });
    return ConnectionCardInfoSchema.parseAsync(r);
  }

  async getOnDeviceMcpCatalog(): Promise<OnDeviceMcpCatalogEntry[]> {
    const r = await apiClient.get('/integrations/on-device-catalog', {
      headers: platformHeaders(),
    });
    return z.array(OnDeviceMcpCatalogEntrySchema).parseAsync(r);
  }

  /** "Connect this app" suggestions for Home, computed server-side from recent screen usage */
  async getIntegrationSuggestions(
    includeOnDevice: boolean
  ): Promise<IntegrationSuggestion[]> {
    // Asked for explicitly, so a backend that predates reconnect and try entries is still readable
    const r = await apiClient.get(
      `/integrations/suggestions?include_reconnects=true&include_tries=true&home_chips=true${
        includeOnDevice ? '&include_on_device=true' : ''
      }`
    );
    return z.array(IntegrationSuggestionSchema).parseAsync(r);
  }

  async markIntegrationSuggestionRemoved(toolkitSlug: string): Promise<void> {
    await apiClient.post(
      `/integrations/suggestions/${encodeURIComponent(toolkitSlug)}/removed`
    );
  }

  async dismissIntegrationSuggestion(toolkitSlug: string): Promise<void> {
    await apiClient.post(`/integrations/suggestions/${toolkitSlug}/dismiss`);
  }

  /** Get all connected integration accounts, optionally probing live status against Composio */
  async getConnectedAccounts(
    toolkit_name?: string,
    probe = false
  ): Promise<ConnectedAccount[]> {
    const params: Record<string, string | boolean> = {};
    if (toolkit_name) params.toolkit_name = toolkit_name;
    if (probe) params.probe = true;
    const r = await apiClient.get('/integrations/accounts', {
      params,
      headers: platformHeaders(),
    });
    return z.array(ConnectedAccountSchema).parseAsync(r);
  }

  /** Toggle the user_enabled state of an integration */
  async toggleIntegration(
    toolkit_name: string,
    userEnabled: boolean
  ): Promise<ConnectedAccount> {
    const body: ToggleIntegrationRequest = {
      toolkit_name,
      user_enabled: userEnabled,
    };
    const r = await apiClient.patch('/integrations/accounts/toggle', body, {
      headers: platformHeaders(),
    });
    return ConnectedAccountSchema.parseAsync(r);
  }

  /** Initiate OAuth connection for an integration */
  async connectIntegration(
    toolkit_name: string,
    account_label?: string | null,
    returnTo?: OAuthReturnTo,
    account_id?: string | null,
    quickGenerateInitialSuggestions = false
  ): Promise<{
    redirect_url: string | null;
    message: string;
    success: boolean;
    account_id?: string | null;
    flow_id?: string | null;
  }> {
    const r = await apiClient.post('/integrations/accounts/connect', {
      toolkit_name,
      account_label: account_label ?? null,
      account_id: account_id ?? null,
      return_path: returnTo?.path ?? null,
      return_channel: returnTo?.channel ?? null,
      quick_generate_initial_suggestions: quickGenerateInitialSuggestions,
    });
    return ConnectIntegrationResponseSchema.parseAsync(r);
  }

  async getInitialSuggestionsStatus(): Promise<InitialSuggestionsStatus> {
    const r = await apiClient.get('/action-cards/initial-suggestions');
    return (await InitialSuggestionsStatusResponseSchema.parseAsync(r)).status;
  }

  async startInitialSuggestions(): Promise<void> {
    await apiClient.post('/action-cards/initial-suggestions');
  }

  /**
   * Abort an in-progress OAuth flow (e.g. the user closed the popup) so the
   * account doesn't linger in 'pending'. Mirrors MCP's cancel-oauth; scoped to
   * the flow_id from the connect response so it can't abort an immediate retry.
   */
  async cancelPendingOAuth(accountId: string, flowId: string): Promise<void> {
    await apiClient.post(
      `/integrations/accounts/${accountId}/cancel-oauth?flow_id=${encodeURIComponent(flowId)}`
    );
  }

  /** Disconnect an integration */
  async disconnectIntegration(
    accountId: string
  ): Promise<DisconnectIntegrationResponse> {
    const r = await apiClient.delete(`/integrations/accounts/${accountId}`);
    return DisconnectIntegrationResponseSchema.parseAsync(r);
  }

  /** Update account label */
  async updateAccountLabel(
    accountId: string,
    newLabel: string
  ): Promise<ConnectedAccount> {
    const r = await apiClient.patch(
      `/integrations/accounts/${accountId}/label`,
      {
        account_label: newLabel,
      },
      { headers: platformHeaders() }
    );
    return ConnectedAccountSchema.parseAsync(r);
  }

  /** Toggle integration by account ID (for multi-account support) */
  async toggleIntegrationById(
    accountId: string,
    userEnabled: boolean
  ): Promise<ConnectedAccount> {
    const r = await apiClient.patch(
      `/integrations/accounts/${accountId}/toggle`,
      {
        user_enabled: userEnabled,
      },
      { headers: platformHeaders() }
    );
    return ConnectedAccountSchema.parseAsync(r);
  }

  /** Send a previously created email draft */
  async sendDraft(
    draftId: string,
    toolkitName: string,
    accountLabel: string
  ): Promise<void> {
    await apiClient.post('/integrations/send-draft', {
      draft_id: draftId,
      toolkit_name: toolkitName,
      account_label: accountLabel,
    });
  }

  /** Fetch full details of an email draft */
  async getDraft(
    draftId: string,
    toolkitName: string,
    accountLabel: string
  ): Promise<EmailDraft> {
    const r = await apiClient.get('/integrations/email-draft', {
      params: {
        draft_id: draftId,
        toolkit_name: toolkitName,
        account_label: accountLabel,
      },
    });
    return EmailDraftSchema.parseAsync(r);
  }

  /** Update an email draft's content */
  async updateDraft(
    draftId: string,
    toolkitName: string,
    accountLabel: string,
    updates: EmailDraft
  ): Promise<void> {
    await apiClient.post('/integrations/update-draft', {
      draft_id: draftId,
      toolkit_name: toolkitName,
      account_label: accountLabel,
      ...updates,
    });
  }
}

const CALENDAR_VALIDATION_ERROR_THROTTLE_MS = 5 * 60 * 1_000;
const calendarValidationErrorCache = new Map<string, number>();

/** The Calendar API class for fetching calendar events */
class CalendarAPI {
  /** Get calendar events, optionally filtered (day window, max count, excluded types/statuses/solo events) */
  async getEvents(
    filters?: CalendarEventFilterSettings
  ): Promise<CalendarEvent[]> {
    return (await this.getEventsReport(filters)).events;
  }

  async getEventsReport(
    filters?: CalendarEventFilterSettings
  ): Promise<{ events: CalendarEvent[]; invalidCount: number }> {
    const r = await apiClient.get('/calendar/events', {
      headers: {
        TZ: getDeviceTimeZone(),
      },
      params: filters
        ? buildCalendarEventFilterQueryParams(filters)
        : undefined,
    });
    const { events, validationErrors } =
      await CalendarEventsResponseSchema.parseAsync(r);
    for (const { error } of validationErrors) {
      const key = error.issues
        .map((i) => `${i.path.join('.')}:${i.code}`)
        .join('|');
      const lastSeen = calendarValidationErrorCache.get(key) ?? 0;
      if (Date.now() - lastSeen >= CALENDAR_VALIDATION_ERROR_THROTTLE_MS) {
        calendarValidationErrorCache.set(key, Date.now());
        captureException(error, {
          extra: { source: 'calendar_event_validation' },
        });
      }
    }
    return { events, invalidCount: validationErrors.length };
  }
}

/** The Debug API class for fetching debug information */
class DebugAPI {
  /** Get debug HTML for a chat (system prompt or context) */
  async getChatDebugHtml(chatId: string, type: 'sys' | 'ctx'): Promise<string> {
    const r = await apiClient.get(`/debug/chat/${chatId}/html`, {
      params: { type },
    });
    return z.string().parse(r);
  }

  async getTracesForChat(
    chatId: string,
    signal?: AbortSignal,
    includeTraceIds: boolean = true
  ): Promise<ChatTurnTrace[]> {
    const r = await apiClient.get('/trace', {
      params: { chat_id: chatId, include_trace_ids: includeTraceIds },
      signal,
    });
    const validated = await ChatDebugMessagesResponseSchema.parseAsync(r);
    return validated.chat_turn_traces;
  }

  async getTraceTreeForMessage(
    traceId: string,
    signal?: AbortSignal
  ): Promise<TraceSpan> {
    const r = await apiClient.get(`/trace/${traceId}`, { signal });
    const result = await TraceApiResponseSchema.safeParseAsync(r);
    if (!result.success) {
      console.error('Failed to parse trace tree response:', result.error);
      console.error('Raw response data:', r);
      throw result.error;
    }
    return result.data.tree;
  }

  async getChatCosts(
    chatId: string,
    signal?: AbortSignal
  ): Promise<ChatCostSummary> {
    const r = await apiClient.get(`/debug/chat/${chatId}/costs`, { signal });
    return ChatCostSummarySchema.parseAsync(r);
  }

  async getDebugPayloadSkeletons(
    chatId: string,
    signal?: AbortSignal,
    includeSubagents: boolean = true
  ): Promise<DebugPayloadSkeletonsResponse> {
    const r = await apiClient.get(`/debug/chat/${chatId}/payloads`, {
      params: { include_subagents: includeSubagents },
      signal,
    });
    return DebugPayloadSkeletonsResponseSchema.parseAsync(r);
  }

  async getDebugFullPayload(
    chatId: string,
    messageId: string,
    stepIndex: number
  ): Promise<DebugFullPayload> {
    const r = await apiClient.get(
      `/debug/chat/${chatId}/payloads/${messageId}/steps/${stepIndex}/export`
    );
    return DebugFullPayloadSchema.parseAsync(r);
  }
}

/** Internal-only view over inbound MCP server tool calls (see /debug/mcp on the backend) */
class McpInfoAPI {
  async listCalls(
    params: {
      client_name?: string;
      tool_name?: string;
      since?: string;
      cursor?: string;
      limit?: number;
    },
    signal?: AbortSignal
  ): Promise<McpCallListResponse> {
    const r = await apiClient.get('/debug/mcp/calls', { params, signal });
    return McpCallListResponseSchema.parseAsync(r);
  }

  async getCall(callId: string, signal?: AbortSignal): Promise<McpCallDetail> {
    const r = await apiClient.get(`/debug/mcp/calls/${callId}`, { signal });
    return McpCallDetailSchema.parseAsync(r);
  }

  async getSummary(
    since: string,
    signal?: AbortSignal
  ): Promise<McpUsageSummary> {
    const r = await apiClient.get('/debug/mcp/summary', {
      params: { since },
      signal,
    });
    return McpUsageSummarySchema.parseAsync(r);
  }
}

export const AxonDeviceSchema = z.object({
  device_slug: z.string(),
  platform: z.string(),
  integration_count: z.number(),
  last_active_at: z.string(),
  is_this_device: z.boolean(),
});
export type AxonDevice = z.infer<typeof AxonDeviceSchema>;

const AxonDevicesResponseSchema = z.object({
  devices: z.array(AxonDeviceSchema),
});

/** The user's devices that share on-device integrations with the agent */
class AxonDevicesAPI {
  async list(currentDeviceId?: string): Promise<AxonDevice[]> {
    const r = await apiClient.get('/axon/devices', {
      params: { current_device_id: currentDeviceId },
    });
    const { devices } = await AxonDevicesResponseSchema.parseAsync(r);
    return devices;
  }

  async revoke(deviceSlug: string): Promise<void> {
    await apiClient.post(
      `/axon/devices/${encodeURIComponent(deviceSlug)}/revoke`
    );
  }
}

class HiveAPI {
  async getThreadByChat(chatId: string, signal?: AbortSignal) {
    const r = await apiClient.get(`/hivemind/threads/by-chat/${chatId}`, {
      signal,
    });
    return r;
  }

  async getGroups(signal?: AbortSignal) {
    return apiClient.get('/hivemind/groups', { signal });
  }

  async saveGroup(name: string, memberEmails: string[]) {
    await apiClient.put(`/hivemind/groups/${encodeURIComponent(name)}`, {
      member_emails: memberEmails,
    });
  }

  async deleteGroup(name: string) {
    await apiClient.delete(`/hivemind/groups/${encodeURIComponent(name)}`);
  }
}

const SharedChatFileSchema = z.object({
  url: z.string(),
  content_type: z.string(),
  filename: z.string(),
  id: z.string(),
  size: z.number(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  entity: z.string().optional(),
  entityId: z.string().optional(),
  expires_in: z.number().optional(),
});

export type SharedChatFile = z.infer<typeof SharedChatFileSchema>;

/** The Chat API class for back-end chat APIs */
class ChatAPI {
  /** Internal-only: per-turn + compaction cost summary. */
  async getCost(
    chatId: string,
    signal?: AbortSignal
  ): Promise<ChatCostSummary> {
    const r = await apiClient.get(`/debug/chat/${chatId}/costs`, { signal });
    return ChatCostSummarySchema.parseAsync(r);
  }

  /** Get a shared chat by ID and public token */
  async getSharedChat(
    chatId: string,
    chatPublicToken: string,
    useAuth = false
  ): Promise<Chat> {
    const r = await apiClient.get(`/get_shared_chat/${chatId}`, {
      params: { public_token: chatPublicToken },
      skipAuth: !useAuth,
    });
    return ChatSchema.parseAsync(r);
  }

  async getSharedChatFile(
    chatId: string,
    fileId: string,
    publicToken: string,
    useAuth = false
  ): Promise<SharedChatFile> {
    const r = await apiClient.get(
      `/get_shared_chat/${chatId}/files/${fileId}`,
      {
        params: { public_token: publicToken },
        skipAuth: !useAuth,
      }
    );
    return SharedChatFileSchema.parseAsync(r);
  }
}

const RecentInputsSummaryResponseSchema = z.object({
  summary: z.string(),
  suggested_routine_slugs: z.array(z.string()).default([]),
});

export interface RecentInputsSummary {
  summary: string;
  suggestedRoutineSlugs: string[];
}

/** The Inputs API class for back-end inputs/context APIs */
class InputsAPI {
  /** Delete inputs older than timestamp */
  async deleteInputs(timestamp: number, endTimestamp?: number): Promise<void> {
    await apiClient.post('/delete/inputs', {
      timestamp,
      end_timestamp: endTimestamp,
    });
  }

  /** Get recent inputs summary (plus context-ranked routine slugs) since cutoff date */
  async getRecentSummary(cutoffDate: Date): Promise<RecentInputsSummary> {
    try {
      const r = await apiClient.get('/recent-inputs-summary', {
        params: { cutoff_date: cutoffDate.toISOString() },
      });
      const parsed = await RecentInputsSummaryResponseSchema.parseAsync(r);
      return {
        summary: parsed.summary,
        suggestedRoutineSlugs: parsed.suggested_routine_slugs,
      };
    } catch (error) {
      console.error('Failed to get recent inputs summary:', error);
      return { summary: '', suggestedRoutineSlugs: [] };
    }
  }
}

const ChannelConnectionResponseSchema = z.object({
  identifier: z.string(),
  has_active_conversation: z.boolean().optional().default(false),
  conversation_link: z.string().optional().default(''),
});

type ChannelConnectionResponse = z.infer<
  typeof ChannelConnectionResponseSchema
>;

const LinkCodeResponseSchema = z.object({
  code: z.string(),
  deep_link: z.string(),
  qr_svg: z.string(),
  expires_in_seconds: z.number(),
});

type LinkCodeResponse = z.infer<typeof LinkCodeResponseSchema>;

const CHANNEL_CONNECTION_API_PATH = {
  imessage: '/imessage/connection',
  whatsapp_business: '/whatsapp/connection',
} as const;

const CHANNEL_LINK_CODE_API_PATH = {
  imessage: '/imessage/link-code',
  whatsapp_business: '/whatsapp/link-code',
} as const;

const CHANNEL_PERSONA_API_PATH = '/channels/persona';
const CHANNEL_SETTINGS_REQUEST_TIMEOUT_MS = 5_000;

const PersonaSectionSchema = z.object({
  text: z.string(),
  is_override: z.boolean(),
});

export type ChannelPersonaSection = z.infer<typeof PersonaSectionSchema>;

const ChannelPersonaResponseSchema = z.object({
  voice: PersonaSectionSchema,
  examples: PersonaSectionSchema,
});

type ChannelPersonaResponse = z.infer<typeof ChannelPersonaResponseSchema>;

/** A string sets the section, null clears it to the default, absent leaves it unchanged. */
export type ChannelPersonaUpdate = {
  voice?: string | null;
  examples?: string | null;
};

type MessagingChannelType = keyof typeof CHANNEL_CONNECTION_API_PATH;

class MessagingChannelsAPI {
  async getConnection(
    channelType: MessagingChannelType
  ): Promise<ChannelConnectionResponse | null> {
    try {
      const r = await apiClient.get(CHANNEL_CONNECTION_API_PATH[channelType], {
        signal: AbortSignal.timeout(CHANNEL_SETTINGS_REQUEST_TIMEOUT_MS),
        retryOptions: { retries: 0 },
      });
      return ChannelConnectionResponseSchema.parse(r);
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) {
        return null;
      }
      throw err;
    }
  }

  async deleteConnection(channelType: MessagingChannelType): Promise<void> {
    await apiClient.delete(CHANNEL_CONNECTION_API_PATH[channelType]);
  }

  async createLinkCode(
    channelType: MessagingChannelType
  ): Promise<LinkCodeResponse> {
    const r = await apiClient.post(
      CHANNEL_LINK_CODE_API_PATH[channelType],
      {},
      {
        signal: AbortSignal.timeout(10000),
        retryOptions: { retries: 0 },
      }
    );
    return LinkCodeResponseSchema.parse(r);
  }

  async getPersona(): Promise<ChannelPersonaResponse> {
    const r = await apiClient.get(CHANNEL_PERSONA_API_PATH, {
      signal: AbortSignal.timeout(CHANNEL_SETTINGS_REQUEST_TIMEOUT_MS),
      retryOptions: { retries: 0 },
    });
    return ChannelPersonaResponseSchema.parse(r);
  }

  async updatePersona(
    update: ChannelPersonaUpdate
  ): Promise<ChannelPersonaResponse> {
    const r = await apiClient.put(CHANNEL_PERSONA_API_PATH, update);
    return ChannelPersonaResponseSchema.parse(r);
  }
}

const SimulatorStatusSchema = z.object({ active: z.boolean() });

export type SimulatorStatus = z.infer<typeof SimulatorStatusSchema>;

const SimulatorChannelSchema = z.enum(['imessage', 'whatsapp']);

export type SimulatorChannel = z.infer<typeof SimulatorChannelSchema>;

const SimulatorChannelReadinessSchema = z.object({
  ready: z.boolean(),
  missing_settings: z.array(z.string()),
  sender_identifier: z.string(),
});

const SimulatorSessionSchema = z.object({
  channels: z.record(SimulatorChannelSchema, SimulatorChannelReadinessSchema),
});

export type SimulatorSession = z.infer<typeof SimulatorSessionSchema>;

const SimulatorEventSchema = z.object({
  seq: z.number(),
  at: z.string(),
  channel: SimulatorChannelSchema,
  kind: z.enum([
    'inbound',
    'webhook_status',
    'outbound',
    'reaction',
    'read',
    'typing',
    'contact_card',
  ]),
  message_id: z.string().nullable(),
  text: z.string(),
  attachment: z
    .object({
      media_id: z.string(),
      filename: z.string(),
      mime_type: z.string(),
    })
    .nullable(),
  file: z
    .object({
      url: z.string(),
      filename: z.string(),
      content_type: z.string(),
    })
    .nullable(),
  reply_to: z
    .object({ message_id: z.string(), part_index: z.number() })
    .nullable(),
  link_preview: z.boolean(),
  emoji: z.string().nullable(),
  webhook_status: z.number().nullable(),
  webhook_body: z.unknown().nullable(),
  request: z
    .object({
      method: z.string(),
      url: z.string(),
      body: z.unknown().nullable(),
    })
    .nullable(),
});

export type SimulatorEvent = z.infer<typeof SimulatorEventSchema>;

const SimulatorEventPageSchema = z.object({
  generation: z.string(),
  events: z.array(SimulatorEventSchema),
});

export type SimulatorEventPage = z.infer<typeof SimulatorEventPageSchema>;

const SimulatorSendResultSchema = z.object({
  message_id: z.string(),
  webhook_status: z.number(),
});

export type SimulatorSendResult = z.infer<typeof SimulatorSendResultSchema>;

const SimulatorLinkCodeSchema = z.object({
  code: z.string(),
  message: z.string(),
});

export type SimulatorLinkCode = z.infer<typeof SimulatorLinkCodeSchema>;

const CHANNEL_SIMULATOR_API_PATH = '/channels/simulator';

class ChannelSimulatorAPI {
  async getStatus(): Promise<SimulatorStatus> {
    const r = await apiClient.get(`${CHANNEL_SIMULATOR_API_PATH}/status`, {
      signal: AbortSignal.timeout(CHANNEL_SETTINGS_REQUEST_TIMEOUT_MS),
      retryOptions: { retries: 0 },
    });
    return SimulatorStatusSchema.parse(r);
  }

  async getSession(sessionId: string): Promise<SimulatorSession> {
    const r = await apiClient.get(
      `${CHANNEL_SIMULATOR_API_PATH}/sessions/${sessionId}`,
      { retryOptions: { retries: 0 } }
    );
    return SimulatorSessionSchema.parse(r);
  }

  async listEvents(
    sessionId: string,
    after: number,
    generation: string | null
  ): Promise<SimulatorEventPage> {
    const params = new URLSearchParams({ after: String(after) });
    if (generation) params.set('generation', generation);
    const r = await apiClient.get(
      `${CHANNEL_SIMULATOR_API_PATH}/sessions/${sessionId}/events?${params}`,
      { retryOptions: { retries: 0 } }
    );
    return SimulatorEventPageSchema.parse(r);
  }

  async clearEvents(sessionId: string): Promise<void> {
    await apiClient.delete(
      `${CHANNEL_SIMULATOR_API_PATH}/sessions/${sessionId}/events`
    );
  }

  async sendMessage(
    sessionId: string,
    channel: SimulatorChannel,
    text: string,
    attachment?: File
  ): Promise<SimulatorSendResult> {
    const body = new FormData();
    body.append('text', text);
    if (attachment) body.append('attachment', attachment, attachment.name);
    const r = await apiClient.postFormData(
      `${CHANNEL_SIMULATOR_API_PATH}/sessions/${sessionId}/${channel}/messages`,
      body,
      { retryOptions: { retries: 0 } }
    );
    return SimulatorSendResultSchema.parse(r);
  }

  async createLinkCode(channel: SimulatorChannel): Promise<SimulatorLinkCode> {
    const r = await apiClient.post(
      `${CHANNEL_SIMULATOR_API_PATH}/${channel}/link-code`,
      {},
      { retryOptions: { retries: 0 } }
    );
    return SimulatorLinkCodeSchema.parse(r);
  }
}

const AgentInboxAddressSchema = z.object({ address: z.string() });

const AgentInboxMessageSchema = z.object({
  id: z.string(),
  kind: z.enum(['owner', 'signup', 'held', 'sent']).catch('owner'),
  sender: z.string(),
  recipients: z.array(z.string()),
  subject: z.string(),
  created_at: z.string(),
  chat_id: z.string().nullable(),
});

export type AgentInboxMessage = z.infer<typeof AgentInboxMessageSchema>;

const AgentInboxMessageDetailSchema = AgentInboxMessageSchema.extend({
  body: z.string(),
});

const AgentInboxContactSchema = z.object({
  address: z.string(),
  trusted: z.boolean(),
  instructions: z.string(),
});

export type AgentInboxContact = z.infer<typeof AgentInboxContactSchema>;

class AgentInboxAPI {
  async getAddress(): Promise<string> {
    const r = await apiClient.get('/agent-inbox/address');
    return AgentInboxAddressSchema.parse(r).address;
  }

  async isAvailable(address: string): Promise<boolean> {
    const r = await apiClient.get(
      `/agent-inbox/address/availability?address=${encodeURIComponent(address)}`
    );
    return z.object({ available: z.boolean() }).parse(r).available;
  }

  async rename(address: string): Promise<string> {
    const r = await apiClient.put('/agent-inbox/address', { address });
    return AgentInboxAddressSchema.parse(r).address;
  }

  async listMessages(sent: boolean): Promise<AgentInboxMessage[]> {
    const r = await apiClient.get(`/agent-inbox/messages?sent=${sent}`);
    return z.array(AgentInboxMessageSchema).parse(r);
  }

  async getMessage(id: string) {
    const r = await apiClient.get(`/agent-inbox/messages/${id}`);
    return AgentInboxMessageDetailSchema.parse(r);
  }

  async listContacts(): Promise<AgentInboxContact[]> {
    const r = await apiClient.get('/agent-inbox/contacts');
    return z.array(AgentInboxContactSchema).parse(r);
  }

  async saveContact(contact: AgentInboxContact): Promise<AgentInboxContact> {
    const r = await apiClient.put('/agent-inbox/contacts', contact, {
      emitHttpErrorEvent: false,
    });
    return AgentInboxContactSchema.parse(r);
  }

  async removeContact(address: string): Promise<void> {
    await apiClient.delete(
      `/agent-inbox/contacts/${encodeURIComponent(address)}`
    );
  }
}

class GrowthAPI {
  async getWrapped(
    periodStart?: string,
    periodEnd?: string
  ): Promise<WrappedResponse> {
    const params: Record<string, string> = {};
    if (periodStart) params.period_start = periodStart;
    if (periodEnd) params.period_end = periodEnd;
    const r = await apiClient.get('/growth/wrapped', { params });
    return WrappedResponseSchema.parseAsync(r);
  }

  async createShare(
    periodStart?: string,
    periodEnd?: string
  ): Promise<{ shareId: string }> {
    const body: Record<string, string> = {};
    if (periodStart) body.period_start = periodStart;
    if (periodEnd) body.period_end = periodEnd;
    const r = await apiClient.post<{ share_id: string }>(
      '/growth/wrapped/share',
      body
    );
    return { shareId: r.share_id };
  }

  async getSharedWrapped(shareId: string): Promise<WrappedResponse> {
    const r = await apiClient.get(`/growth/wrapped/share/${shareId}`, {
      skipAuth: true,
    });
    return WrappedResponseSchema.parseAsync(r);
  }
}

const SharedSiteSchema = z.object({
  filename: z.string(),
  visibility: z.enum(['private', 'team', 'public']),
  // `html` is the legacy field name for inline HTML or Canvas content on team shares.
  url: z.string().nullish(),
  html: z.string().nullish(),
});
export type SharedSite = z.infer<typeof SharedSiteSchema>;

class PublishedSitesAPI {
  async getSharedSite(token: string, useAuth = false): Promise<SharedSite> {
    const r = await apiClient.get(`/artifact/${token}`, {
      skipAuth: !useAuth,
    });
    return SharedSiteSchema.parseAsync(r);
  }
}

/** Adapts the desktop API client's method/path/body/signal shape to the
 * `@littlebird/integrations` package's transport contract — the package
 * alone builds `/tools/providers/...` paths, this just carries the request. */
function sendBackendRequest(request: ProviderToolsRequest): Promise<unknown> {
  if (request.method === 'GET') {
    return apiClient.get(request.path, { signal: request.signal });
  }
  return apiClient.put(request.path, request.body, { signal: request.signal });
}

export type FileLinkMeta = z.infer<typeof FileLinkMetaSchema>;

class FileLinksAPI {
  async getMeta(token: string, useAuth = false): Promise<FileLinkMeta> {
    const response = await apiClient.get(`/f/${token}/meta`, {
      skipAuth: !useAuth,
    });
    return FileLinkMetaSchema.parseAsync(response);
  }
}

/** Export for the API Routes */
export const backendApis = {
  meetingNotes: new MeetingNotesAPI(),
  user: new UserAPI(),
  todo: new TodoAPI(),
  cardWatchers: new CardWatcherAPI(),
  feedback: new FeedbackAPI(),
  invite: new InviteAPI(),
  referral: new ReferralAPI(),
  subscription: new SubscriptionAPI(),
  journal: new JournalAPI(),
  worldModelDoc: new WorldModelDocAPI(),
  integrations: new IntegrationsAPI(),
  calendar: new CalendarAPI(),
  debug: new DebugAPI(),
  mcpInfo: new McpInfoAPI(),
  axonDevices: new AxonDevicesAPI(),
  hive: new HiveAPI(),
  chat: new ChatAPI(),
  inputs: new InputsAPI(),
  mcp: createMCPClient(apiClient),
  providerTools: createProviderToolsApi(sendBackendRequest),
  messagingChannels: new MessagingChannelsAPI(),
  channelSimulator: new ChannelSimulatorAPI(),
  agentInbox: new AgentInboxAPI(),
  growth: new GrowthAPI(),
  publishedSites: new PublishedSitesAPI(),
  fileLinks: new FileLinksAPI(),
};
